import React from 'react';

// ─── LAYOUT DEFINITIONS ───
export const GALLERY_LAYOUTS = [
  { id: 'masonry', name: 'Masonry', icon: '🧱', desc: 'Pinterest-style waterfall' },
  { id: 'elegant-frames', name: 'Elegant Frames', icon: '🖼', desc: 'Gold-framed classic gallery' },
  { id: 'polaroid', name: 'Polaroid Wall', icon: '📷', desc: 'Scattered polaroid snapshots' },
  { id: 'mosaic', name: 'Mosaic Grid', icon: '🎨', desc: 'Mixed-size collage tiles' },
  { id: 'filmstrip', name: 'Film Strip', icon: '🎬', desc: 'Cinema-style film reel' },
  { id: 'scrapbook', name: 'Scrapbook', icon: '📒', desc: 'Angled with tape & pins' },
];

// ─── DOWNLOAD HELPER ───
function downloadMedia(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'moment';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ─── RENDER MEDIA (shared helper) ───
function Media({ post, style }) {
  if (post.media_type === 'video') {
    return <video src={post.media_url} controls style={{ ...style, objectFit: 'cover', display: 'block' }} />;
  }
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <img src={post.media_url} alt={post.caption || ''} style={{ ...style, objectFit: 'cover', display: 'block' }} />
    </div>
  );
}

// ─── MASONRY LAYOUT ───
function MasonryLayout({ gallery, accent, onEdit, onDelete, isHost, isOwnPost }) {
  return (
    <div style={{ columns: '3 240px', columnGap: '16px' }}>
      {gallery.map((post, idx) => {
        const sizes = ['small', 'medium', 'large'];
        const frameSize = post.frame_size || sizes[idx % 3];
        const heights = { small: '180px', medium: '260px', large: '340px' };
        return (
          <div key={post.id} style={{
            breakInside: 'avoid', marginBottom: '16px',
            borderRadius: '12px', overflow: 'hidden',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            transition: 'transform 0.3s', cursor: 'pointer', position: 'relative'
          }}>
            <Media post={post} style={{ width: '100%', height: heights[frameSize] }} />
            <Overlay post={post} onEdit={onEdit} onDelete={onDelete} isHost={isHost} isOwnPost={isOwnPost} />
          </div>
        );
      })}
    </div>
  );
}

// ─── ELEGANT FRAMES LAYOUT ───
function ElegantFramesLayout({ gallery, accent, onEdit, onDelete, isHost, isOwnPost }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '28px', padding: '10px' }}>
      {gallery.map(post => (
        <div key={post.id} style={{
          padding: '12px', borderRadius: '4px', position: 'relative',
          background: 'linear-gradient(145deg, #c9a84c, #a3853b, #c9a84c, #e8d59a, #c9a84c)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
        }}>
          <div style={{
            padding: '6px', background: '#1a1714',
            boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.5)',
          }}>
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <Media post={post} style={{ width: '100%', height: '220px' }} />
              <Overlay post={post} onEdit={onEdit} onDelete={onDelete} isHost={isHost} isOwnPost={isOwnPost} />
            </div>
          </div>
          {post.caption && (
            <div style={{ textAlign: 'center', padding: '8px 4px 2px', fontSize: '11px', color: '#1a1714', fontWeight: 600, fontStyle: 'italic', fontFamily: "'Playfair Display', serif" }}>
              {post.caption}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── POLAROID LAYOUT ───
function PolaroidLayout({ gallery, accent, onEdit, onDelete, isHost, isOwnPost }) {
  const rotations = [-3, 2, -1.5, 3, -2, 1, -3.5, 2.5, -1, 3.5, -2.5, 1.5];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center', padding: '20px' }}>
      {gallery.map((post, idx) => {
        const rot = rotations[idx % rotations.length];
        return (
          <div key={post.id} style={{
            width: '220px', padding: '12px 12px 40px 12px',
            background: '#fff', borderRadius: '2px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25), 0 1px 4px rgba(0,0,0,0.15)',
            transform: `rotate(${rot}deg)`,
            transition: 'transform 0.3s, box-shadow 0.3s',
            cursor: 'pointer', position: 'relative'
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = `rotate(0deg) scale(1.05)`; e.currentTarget.style.zIndex = '10'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = `rotate(${rot}deg)`; e.currentTarget.style.zIndex = '1'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)'; }}
          >
            <Media post={post} style={{ width: '100%', height: '200px' }} />
            <div style={{ position: 'absolute', bottom: '10px', left: '12px', right: '12px' }}>
              <p style={{ fontSize: '12px', color: '#444', fontFamily: "'Playfair Display', serif", textAlign: 'center', fontStyle: 'italic' }}>
                {post.caption || post.user_name || ''}
              </p>
            </div>
            {/* Tape effect */}
            <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%) rotate(1deg)', width: '60px', height: '20px', background: 'rgba(200,180,130,0.5)', borderRadius: '2px' }} />
            {(isHost || (isOwnPost && isOwnPost(post))) && (
              <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '4px' }}>
                {isHost && onEdit && <HostBtn icon="edit" onClick={() => onEdit(post)} />}
                {onDelete && <HostBtn icon="delete" onClick={() => onDelete(post)} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── MOSAIC LAYOUT ───
function MosaicLayout({ gallery, accent, onEdit, onDelete, isHost, isOwnPost }) {
  // Create a varied grid pattern
  const getSpan = (idx) => {
    const pattern = [
      { col: 'span 2', row: 'span 2' }, // large
      { col: 'span 1', row: 'span 1' }, // small
      { col: 'span 1', row: 'span 1' }, // small
      { col: 'span 1', row: 'span 2' }, // tall
      { col: 'span 2', row: 'span 1' }, // wide
      { col: 'span 1', row: 'span 1' }, // small
    ];
    return pattern[idx % pattern.length];
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridAutoRows: '140px', gap: '8px' }}>
      {gallery.map((post, idx) => {
        const span = getSpan(idx);
        return (
          <div key={post.id} style={{
            gridColumn: span.col, gridRow: span.row,
            borderRadius: '10px', overflow: 'hidden', position: 'relative',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}>
            <Media post={post} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }} />
            <Overlay post={post} onEdit={onEdit} onDelete={onDelete} isHost={isHost} compact />
          </div>
        );
      })}
    </div>
  );
}

// ─── FILMSTRIP LAYOUT ───
function FilmstripLayout({ gallery, accent, onEdit, onDelete, isHost, isOwnPost }) {
  return (
    <div style={{ position: 'relative' }}>
      {/* Sprocket holes */}
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: '8px', width: '24px', display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px', zIndex: 2 }}>
        {gallery.map((_, i) => <div key={i} style={{ width: '18px', height: '12px', borderRadius: '3px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.1)' }} />)}
      </div>
      <div style={{ position: 'absolute', top: 0, bottom: 0, right: '8px', width: '24px', display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px', zIndex: 2 }}>
        {gallery.map((_, i) => <div key={i} style={{ width: '18px', height: '12px', borderRadius: '3px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.1)' }} />)}
      </div>
      {/* Film frames */}
      <div style={{ background: 'rgba(20,20,20,0.9)', borderRadius: '8px', padding: '16px 44px', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {gallery.map(post => (
            <div key={post.id} style={{
              display: 'flex', gap: '16px', alignItems: 'center',
              padding: '8px', borderRadius: '6px',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              position: 'relative'
            }}>
              <div style={{ width: '200px', height: '130px', flexShrink: 0, borderRadius: '4px', overflow: 'hidden', border: '2px solid rgba(255,255,255,0.1)' }}>
                <Media post={post} style={{ width: '100%', height: '100%' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {post.caption && <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontFamily: "'Playfair Display', serif", marginBottom: '6px' }}>{post.caption}</p>}
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>{post.user_name} · {new Date(post.created_at).toLocaleDateString()}</div>
                {(isHost || (isOwnPost && isOwnPost(post))) && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    {isHost && onEdit && <HostBtn icon="edit" onClick={() => onEdit(post)} />}
                    {onDelete && <HostBtn icon="delete" onClick={() => onDelete(post)} />}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SCRAPBOOK LAYOUT ───
function ScrapbookLayout({ gallery, accent, onEdit, onDelete, isHost, isOwnPost }) {
  const rotations = [-4, 3, -2, 5, -3, 2, -5, 4, -1, 3];
  const decorations = ['📌', '❤️', '⭐', '🌟', '✨', '💫', '🎀', '🌺', '🦋', '💝'];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', padding: '20px', background: 'repeating-linear-gradient(0deg, transparent, transparent 28px, rgba(255,255,255,0.03) 28px, rgba(255,255,255,0.03) 29px)' }}>
      {gallery.map((post, idx) => {
        const rot = rotations[idx % rotations.length];
        const deco = decorations[idx % decorations.length];
        const isWide = idx % 5 === 0;
        return (
          <div key={post.id} style={{
            width: isWide ? '320px' : '200px', position: 'relative',
            transform: `rotate(${rot}deg)`,
            transition: 'transform 0.3s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'rotate(0deg) scale(1.06)'; e.currentTarget.style.zIndex = '10'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = `rotate(${rot}deg)`; e.currentTarget.style.zIndex = '1'; }}
          >
            {/* Pin */}
            <div style={{ position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)', zIndex: 3, fontSize: '16px' }}>{deco}</div>
            <div style={{
              padding: '8px', background: 'rgba(255,255,255,0.95)',
              borderRadius: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
              <div style={{ borderRadius: '2px', overflow: 'hidden', position: 'relative' }}>
                <Media post={post} style={{ width: '100%', height: isWide ? '200px' : '180px' }} />
                {(isHost || (isOwnPost && isOwnPost(post))) && (
                  <div style={{ position: 'absolute', top: '6px', right: '6px', display: 'flex', gap: '4px' }}>
                    {isHost && onEdit && <HostBtn icon="edit" onClick={() => onEdit(post)} />}
                    {onDelete && <HostBtn icon="delete" onClick={() => onDelete(post)} />}
                  </div>
                )}
              </div>
              <div style={{ padding: '6px 2px 2px' }}>
                <p style={{ fontSize: '12px', color: '#333', fontFamily: "'Playfair Display', serif", fontStyle: 'italic', textAlign: 'center' }}>
                  {post.caption || post.user_name || ''}
                </p>
              </div>
            </div>
            {/* Washi tape */}
            {idx % 3 === 0 && <div style={{ position: 'absolute', top: '-4px', right: '20px', width: '50px', height: '14px', background: `${accent || '#E3425C'}40`, transform: 'rotate(8deg)', borderRadius: '1px' }} />}
          </div>
        );
      })}
    </div>
  );
}

// ─── SHARED OVERLAY ───
function Overlay({ post, onEdit, onDelete, isHost, compact, isOwnPost }) {
  const canEdit = isHost && onEdit;
  const canDelete = (isHost && onDelete) || (isOwnPost && isOwnPost(post) && onDelete);
  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: compact ? '8px' : '12px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 0 }}>
          {post.caption && <p style={{ color: 'white', fontSize: compact ? '11px' : '13px', marginBottom: '2px', textShadow: '0 1px 4px rgba(0,0,0,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.caption}</p>}
          <div style={{ fontSize: compact ? '9px' : '11px', color: 'rgba(255,255,255,0.6)' }}>{post.user_name}</div>
        </div>
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); downloadMedia(post.media_url, post.caption || 'moment'); }} title="Download" style={{
            width: '26px', height: '26px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', transition: 'all 0.2s'
          }}>⬇</button>
          {canEdit && <HostBtn icon="edit" onClick={() => onEdit(post)} />}
          {canDelete && <HostBtn icon="delete" onClick={() => onDelete(post)} />}
        </div>
      </div>
    </div>
  );
}

// ─── HOST ACTION BUTTON ───
function HostBtn({ icon, onClick }) {
  const isDelete = icon === 'delete';
  return (
    <button onClick={e => { e.stopPropagation(); onClick(); }} style={{
      width: '26px', height: '26px', borderRadius: '50%',
      background: isDelete ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.25)',
      border: 'none', color: 'white', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', transition: 'all 0.2s'
    }}>{isDelete ? '✕' : '✎'}</button>
  );
}

// ─── MAIN EXPORT ───
export default function GalleryWall({ gallery, layout = 'masonry', accent, onEdit, onDelete, isHost = false, isOwnPost }) {
  const layoutMap = {
    'masonry': MasonryLayout,
    'elegant-frames': ElegantFramesLayout,
    'polaroid': PolaroidLayout,
    'mosaic': MosaicLayout,
    'filmstrip': FilmstripLayout,
    'scrapbook': ScrapbookLayout,
  };
  const LayoutComponent = layoutMap[layout] || MasonryLayout;
  return <LayoutComponent gallery={gallery} accent={accent} onEdit={onEdit} onDelete={onDelete} isHost={isHost} isOwnPost={isOwnPost} />;
}
